import type { SelectOption } from '@common/types/templateFormFields';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { useRef } from 'react';
import { errorSnackbar } from '@/utils/snackbar';
import SelectField from './SelectField';
import type { OptionEditorFieldData, OptionErrorType } from './types';

interface QuestionSelectComponentProps {
    fromConditional?: boolean;
    fieldData?: OptionEditorFieldData;
    selectOptions: SelectOption[];
    isSuperAdmin?: boolean;
    error: OptionErrorType[];
    onError: (error: OptionErrorType[]) => void;
    onChange: (options: SelectOption[]) => void;
    disabled: boolean;
}
export default function QuestionSelectComponent({
    fromConditional,
    fieldData,
    selectOptions,
    isSuperAdmin,
    error,
    onError,
    onChange,
    disabled,
}: QuestionSelectComponentProps) {
    const dragIndex = useRef<number | undefined>(undefined);

    const handleOnChange = (value: string, index: number) => {
        const sameOption = selectOptions.find((op) => op?.selectItemLabel === value);
        if (sameOption) {
            onError([...error, { message: 'Option already exists', state: true, index: index }]);
        } else {
            onError(error.filter((err) => err.index !== index));
        }
        const updatedChoices = selectOptions.map((op, i) => {
            if (i === index) {
                return {
                    ...op,
                    selectItemKey: value,
                    selectItemLabel: value,
                    selectItemValue: value,
                };
            }
            return op;
        });

        onChange(updatedChoices);
    };

    const handleOnRemove = (index: number) => {
        if (selectOptions.length <= 2) {
            //Display error message is options are less than 2
            errorSnackbar('Deletion failed. A minimum of two options is required.');
            return;
        }
        const updatedChoices = selectOptions.filter((_option, i) => i !== index);
        onError(error.filter((err) => err.index !== index)); //clear error message of removed option
        onChange(updatedChoices);
    };

    //On drop swap the elements in the array
    const handleDrop = (dropIndex: number) => {
        const updatedOptions = [...selectOptions];
        if (dragIndex.current !== undefined) {
            //Remove the dragged Item from the array.
            const draggedItem = updatedOptions.splice(dragIndex.current, 1)[0];

            //Add the dragged Item from the array and place it on drop index and update index for rest of the items.
            updatedOptions.splice(dropIndex, 0, draggedItem);

            //update the dragIndex to undefined
            dragIndex.current = undefined;
        }

        onChange(updatedOptions);
    };

    const isDisabledAddButton =
        disabled ||
        selectOptions.some((option) => !option.selectItemLabel) || //check for empty input
        selectOptions.some((option, index) =>
            selectOptions
                .slice(0, index)
                .some((prevOption) => prevOption.selectItemLabel === option.selectItemLabel),
        ); //check for duplicates

    return (
        <Box
            component={'div'}
            onDragOver={(e) => {
                e.preventDefault();
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '20px',
            }}
        >
            {selectOptions.map((select: SelectOption, index: number) => (
                <SelectField
                    fromConditional={fromConditional}
                    key={index}
                    index={index + 1}
                    option={select}
                    onChange={(value: string) => handleOnChange(value, index)}
                    onRemove={() => handleOnRemove(index)}
                    onDragStart={(index: number) => (dragIndex.current = index)}
                    onDrop={(index) => handleDrop(index)}
                    error={error.find((err) => err.index === index)}
                    fieldData={fieldData}
                    isSuperAdmin={isSuperAdmin}
                />
            ))}
            <Button
                disabled={
                    fieldData?.questionSchema?.restrictOptionEdit
                        ? true
                        : isSuperAdmin || fromConditional === true
                          ? false
                          : isDisabledAddButton
                }
                sx={{
                    textTransform: 'initial',
                }}
                variant="text"
                onClick={() =>
                    onChange([
                        ...selectOptions,
                        {
                            selectItemKey: '',
                            selectItemLabel: '',
                            selectItemValue: '',
                            isNewAdded: true,
                        },
                    ])
                }
            >
                <AddIcon /> Add option
            </Button>
        </Box>
    );
}
